import type { WeekdayData } from "@qendo/schedule";
import { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTheme } from "~/providers/ThemeProvider";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

export type WeekdaySelectorProps = {
    value?: WeekdayData | null | undefined;
    onChange?: (days: WeekdayData) => void;
    disabled?: boolean;
};

const DEFAULT_SELECTION: WeekdayData = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
};

export const WeekdaySelector = ({ value, onChange, disabled }: WeekdaySelectorProps) => {
    const { colours, text, border, background } = useTheme();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const size = Math.floor(itemWidth(width, 7));

    const days: WeekdayData = {
        ...DEFAULT_SELECTION,
        ...value,
    };

    const toggle = (day: keyof WeekdayData) => {
        onChange?.({
            ...days,
            [day]: !days[day],
        });
    };

    const makePressable = (key: keyof WeekdayData, name: string) => {
        const selected = days[key];

        const [bodyStyle, textStyle] = disabled
            ? [[{ backgroundColor: colours.button["primary-disabled-background"] }, border.secondary], text.secondary]
            : selected
              ? [[{ backgroundColor: colours.icon.selected }, border.selected], text["inverse-primary"]]
              : [[background.secondary, border.primary], text.primary];

        return (
            <Pressable
                style={[styles.pressable, bodyStyle, { width: size, height: size, borderRadius: size }]}
                disabled={disabled}
                onPress={() => toggle(key)}
            >
                <Text style={[styles.text, textStyle]}>{name}</Text>
            </Pressable>
        );
    };

    return (
        <View onLayout={onLayout} style={[styles.container, border.bottom_primary]}>
            {makePressable("monday", "M")}
            {makePressable("tuesday", "Tu")}
            {makePressable("wednesday", "W")}
            {makePressable("thursday", "Th")}
            {makePressable("friday", "F")}
            {makePressable("saturday", "Sa")}
            {makePressable("sunday", "Su")}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingBottom: 4,
        borderBottomWidth: 1,
    },
    pressable: {
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    text: {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 14,
    },
});
